import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Input, InputNumber, Select, message, Card, Space, Typography, Upload, Progress, Popconfirm } from 'antd';
import { 
  VideoCameraOutlined,  
  FileTextOutlined,  
  PlayCircleOutlined,  
  SaveOutlined,
  UploadOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { Title } = Typography;

const VideoEditor2 = () => {
  const [files, setFiles] = useState([]); 
  const [activeRenders, setActiveRenders] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [selectedVideo, setSelectedVideo] = useState(null); 
  const [exportName, setExportName] = useState('');
  const [resW, setResW] = useState(400); 
  const [resH, setResH] = useState(520); 
  const [bitrate, setBitrate] = useState('2000');
  const [rotate, setRotate] = useState(0); 

  const [processing, setProcessing] = useState(false);

  const CARD_MIN_HEIGHT = '80px'; 
  const CARD_PADDING = '10px 20px'; 

  const fetchData = async () => {
    try {
      const response = await axios.get('/api/box/media/');
      const allMedia = response.data.filter(f => f.file_type === 'video' || f.media_type === 'video');
      const ready = allMedia.filter(f => f.duration > 0).sort((a, b) => b.id - a.id);
      const inProgress = allMedia.filter(f => !f.duration || f.duration === 0);
      setFiles(ready);
      setActiveRenders(inProgress);
    } catch (error) {
      console.error('Greška pri učitavanju');
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('file_type', 'video');
    formData.append('file_size', file.size);
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const response = await axios.post('/api/box/media/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      message.success(`Fajl ${file.name} uspesno otpremljen!`);
      setIsUploading(false);
      onSuccess(response.data);
      fetchData();
    } catch (error) {
      setIsUploading(false);
      onError(error);
      message.error('Greska pri otpremanju');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/box/media/${id}/`);
      message.success("Fajl obrisan");
      if (selectedVideo?.id === id) setSelectedVideo(null);
      fetchData();
    } catch (error) {
      message.error("Greška pri brisanju");
    }
  };

  const selectVideo = (video) => {
    setSelectedVideo(video);
    // Uzimamo ime fajla, čistimo ga od putanje i ekstenzije i dodajemo rezoluciju
    const rawName = video.name.split('/').pop().replace(/\.[^/.]+$/, "");
    setExportName(`${rawName}_${resW}x${resH}`);
  };

  const formatUrl = (input) => {
    if (!input) return null;
    let path = typeof input === 'object' ? (input.file_url || input.file_path || input.thumbnail || null) : input;
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const cleanPath = path.replace(/^\/?(media\/)?/, '');
    return `https://escreen.stand.ledscreen.rs/media/${cleanPath}`;
  };

  const handleStartProcess = async () => {
    if (!exportName) return message.error("Unesi naziv exporta");
    if (!selectedVideo) return message.error("Izaberi video");
    setProcessing(true);
    const payload = {
      export_name: exportName,
      target_bitrate: bitrate,
      fps: 30,
      media_type: 'video',
      process_mode: 'single',
      parent_media_ids: [selectedVideo.id],
      target_resolution: `${resW}x${resH}`,
      rotate: rotate
    };

    try {
      await axios.post('/api/box/process-media/', payload);
      message.success("Render pokrenut!");
      setExportName('');
      fetchData();
    } catch (e) { message.error("Greska pri pokretanju!"); }
    finally { setProcessing(false); }
  };

  return (
    <div style={{ padding: '20px', background: '#0f172a', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      
      <style>{`
        aside, nav, [style*="width: 240px"] { display: none !important; }
        main, .ant-layout-content, [style*="margin-left"] { margin-left: 0 !important; width: 100% !important; max-width: 100% !important; }

        .media-list-item { display: flex; gap: 10px; padding: 12px; background: #0f172a; border: 1px solid #334155; border-radius: 6px; margin-bottom: 8px; cursor: pointer; transition: 0.2s; align-items: center; }
        .media-list-item:hover { border-color: #3b82f6; background: #1e293b; }
        .media-list-item.selected { border-color: #3b82f6; background: rgba(59, 130, 246, 0.1); }
        .item-thumb { width: 80px; height: 50px; background: #000; border-radius: 4px; overflow: hidden; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .item-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .item-info { flex: 1; min-width: 0; }
        .item-name-white { color: #ffffff !important; font-size: 12px; font-weight: bold; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .item-meta-white { color: #94a3b8 !important; font-size: 11px; }
        .control-card { background: #161e2e !important; border: 1px solid #334155 !important; }
        .control-card .ant-card-head-title { color: #ffffff !important; font-size: 11px !important; text-transform: uppercase; font-weight: bold; }
        
        /* FIX ZA BELU BOJU I DUPLA POLJA */
        .dark-input, .dark-input-num { 
          background: #0f172a !important; 
          border: 1px solid #334155 !important; 
          color: white !important; 
        }
        .ant-input-number { width: 100% !important; background: #0f172a !important; border: 1px solid #334155 !important; }
        .ant-input-number-input { color: white !important; height: 32px !important; }
        .ant-input { background: #0f172a !important; border: 1px solid #334155 !important; color: white !important; }

        .dark-select .ant-select-selector { background: #0f172a !important; border: 1px solid #334155 !important; color: white !important; }
        .ant-select-selection-item { color: white !important; }
        .btn-render-big { background: #3b82f6 !important; height: 60px !important; font-weight: bold !important; border: none; margin-top: 10px; font-size: 16px; }
        .section-label { font-size: 12px; color: #64748b !important; font-weight: bold; text-transform: uppercase; margin-bottom: 12px; border-left: 3px solid #3b82f6; padding-left: 10px; }
        .delete-btn-list { color: #64748b; transition: 0.2s; }
        .delete-btn-list:hover { color: #ef4444; }
      `}</style>

      <div style={{ padding: '0px', marginBottom: '20px' }}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', height: CARD_MIN_HEIGHT, display: 'flex', alignItems: 'center' }}>
              <Space size="middle">
                <FileTextOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
                <div>
                  <div style={{ color: '#64748b', fontSize: '12px' }}>MEDIA LIBRARY</div>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{files.length} Videos</div>
                </div>
              </Space>
            </div>
          </Col>

          <Col span={8}>
            <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', height: CARD_MIN_HEIGHT, display: 'flex', alignItems: 'center' }}>
              <Upload customRequest={handleUpload} showUploadList={false} disabled={isUploading} style={{ width: '100%' }}>
                <Space size="middle" style={{ cursor: 'pointer' }}>
                  <UploadOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
                  <div>
                    <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>UPLOAD FILE</div>
                    <div style={{ color: 'white', fontSize: '14px' }}>Add new media</div>
                  </div>
                </Space>
              </Upload>
              {isUploading && (
                <div style={{ flex: 1, marginLeft: '20px' }}>
                  <Progress percent={uploadProgress} size="small" strokeColor="#3b82f6" trailColor="#334155" />
                </div>
              )}
            </div>
          </Col>

          <Col span={8}>
            <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', height: CARD_MIN_HEIGHT, display: 'flex', alignItems: 'center' }}>
              <Space size="middle">
                {activeRenders.length > 0 ? <LoadingOutlined style={{ color: '#3b82f6', fontSize: '24px' }} /> : <CheckCircleOutlined style={{ color: '#22c55e', fontSize: '24px' }} />}
                <div>
                  <div style={{ color: '#64748b', fontSize: '12px' }}>STATUS</div>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{activeRenders.length} rendering</div>
                </div>
              </Space>
            </div>
          </Col>
        </Row>
      </div>

      <div style={{ flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        <div style={{ padding: '15px 20px', borderBottom: '1px solid #334155' }}>
          <Title level={4} style={{ color: '#3b82f6', margin: 0, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '18px' }}>
            <VideoCameraOutlined style={{ marginRight: '8px' }} /> Video Engine Light
          </Title>
        </div>

        <div className="engine-main-layout" style={{ flex: 1, display: 'flex', gap: '20px', padding: '20px', overflow: 'hidden' }}>
          
          <div style={{ flex: '1', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className="section-label">SOURCE MEDIA</div>
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '5px' }}>
              {files.map((item) => (
                <div 
                  key={item.id} 
                  className={`media-list-item ${selectedVideo?.id === item.id ? 'selected' : ''}`} 
                  onClick={() => selectVideo(item)}
                >
                  <div className="item-thumb">
                    {item.thumbnail ? <img src={formatUrl(item.thumbnail)} alt="" /> : <VideoCameraOutlined style={{ color: '#3b82f6' }} />}
                  </div>
                  <div className="item-info">
                    <div className="item-name-white">{item.name}</div>
                    <div className="item-meta-white">{item.resolution} | {item.duration}s</div>
                  </div>
                  <Popconfirm title="Obriši fajl?" onConfirm={(e) => { e.stopPropagation(); handleDelete(item.id); }} onCancel={(e) => e.stopPropagation()}>
                    <DeleteOutlined className="delete-btn-list" onClick={(e) => e.stopPropagation()} />
                  </Popconfirm>
                </div>
              ))}
            </div>
          </div>

          <div style={{ flex: '0 0 250px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="section-label">CONFIGURATION</div>
            <Card title="ROTATION" size="small" className="control-card">
              <Select value={rotate} onChange={setRotate} style={{ width: '100%' }} className="dark-select">
                <Option value={0}>NO ROTATION</Option>
                <Option value={1}>ROTATE 90° CW</Option>
                <Option value={2}>ROTATE 90° CCW</Option>
                <Option value={180}>ROTATE 180°</Option>
              </Select>
            </Card>
            <Card title="RESOLUTION" size="small" className="control-card">
              <Row gutter={8}>
                <Col span={12}>
                  <InputNumber value={resW} onChange={setResW} placeholder="W" controls={false} />
                </Col>
                <Col span={12}>
                  <InputNumber value={resH} onChange={setResH} placeholder="H" controls={false} />
                </Col>
              </Row>
            </Card>
            <Card title="EXPORT SETTINGS" size="small" className="control-card">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input placeholder="Export Name" value={exportName} onChange={e => setExportName(e.target.value)} />
                <Select value={bitrate} onChange={setBitrate} className="dark-select" style={{width: '100%'}}>
                  <Option value="2000">2 Mbps</Option>
                  <Option value="4000">4 Mbps</Option>
                </Select>
              </Space>
            </Card>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleStartProcess} loading={processing} className="btn-render-big" block>
              START PROCESS
            </Button>
          </div>

          <div style={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className="section-label">PREVIEW & DOWNLOAD</div>
            <div style={{ height: '380px', background: '#000', borderRadius: '12px', border: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {selectedVideo ? (
                <video 
                  key={selectedVideo.id} 
                  controls 
                  autoPlay 
                  src={formatUrl(selectedVideo)} 
                  style={{ maxWidth: '100%', maxHeight: '100%' }} 
                />
              ) : (
                <PlayCircleOutlined style={{ fontSize: '48px', color: '#1e293b' }} />
              )}
            </div>
            {selectedVideo && (
              <div style={{ marginTop: '10px', color: '#64748b', fontSize: '11px', textAlign: 'center', padding: '0 10px' }}>
                Desni klik na video -> <b>Save Video As...</b>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default VideoEditor2;